import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActionsTable } from "Common/Components";
import { ProductClassification } from "interfaces";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteProductClasification, getProductClasification } from "services";
import { useProdClassificationStore } from "../stores/ProductClassificationStore";
import { NotificationType, showNotifications } from "helpers/toas_notification";

export const usePClassificationList = () => {
  const { idDelete, setProductClassification, setIsEdit, setIdDelete } =
    useProdClassificationStore();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [clasificationsRows, setClasificationsRows] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [showModalForm, setShowModalForm] = useState<boolean>(false);

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "ID",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("correlativeScreen.table.description"),
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("correlativeScreen.table.actions"),
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: { getValue: () => any }) => getValue(),
      },
    ],
    [t]
  );

  const onHandleCreateClassification = () => {
    setShowModalForm(true);
    setIsEdit(false);
    setProductClassification(null);
  };

  const { data: productClasification } = useQuery({
    queryKey: ["getProductClasification"],
    queryFn: getProductClasification,
  });

  useEffect(() => {
    if (productClasification) {
      setClasificationsRows(createClassificationsRows(productClasification));
    }
  }, [productClasification]);

  const createClassificationsRows = (data: ProductClassification[]) => {
    return data.map((item: ProductClassification, index) => {
      return {
        ID: index + 1,
        description: item.description,
        actions: (
          <ActionsTable
            onClickEdith={() => handleClickEdit(item)}
            onClickDelete={() => handleClickDelete(item)}
          ></ActionsTable>
        ),
      };
    });
  };

  const handleClickDelete = (PrdClassification: ProductClassification) => {
    setShowDeleteModal(true);
    setIdDelete(PrdClassification.id);
  };

  const handleClickEdit = (PrdClassification: ProductClassification) => {
    setShowModalForm(true);
    setIsEdit(true);
    setProductClassification(PrdClassification);
  };

  const onHideModal = () => {
    setShowDeleteModal(false);
    setIdDelete(0);
  };

  const onHideModalForm = () => {
    setShowModalForm(false);
    setIsEdit(false);
    setProductClassification(null);
  };

  const onDeleteConfirm = async () => {
    try {
      const respDelete = await deleteProductClasification(idDelete);

      if (respDelete.code === "200") {
        queryClient.invalidateQueries({
          queryKey: ["getProductClasification"],
        });

        showNotifications(
          NotificationType.SUCCESS,
          t("deleteModal.succedDelete")
        );
        onHideModal();
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("deleteModal.errorDelete") + respDelete.message
        );
      }
    } catch (error) {
      showNotifications(
        NotificationType.ERROR,
        t("deleteModal.errorDelete") + error
      );
    }
  };

  return {
    t,
    columns,
    clasificationsRows,
    showDeleteModal,
    showModalForm,
    onHandleCreateClassification,
    onHideModal,
    onDeleteConfirm,
    onHideModalForm,
  };
};
